// Opportunity entity manifest: Creator dialog + Manage surface. Board row activation opens Manage
// in the shell inspector.
import { ActivityEntityName, DomainType, Opportunity, SalesSecurityRight, SurfaceType } from '@jms/model';
import { RightsService } from '@jms/utils/util.permissions';
import type { EntityManifestCard } from '@workbuddy/services/entities/entity.manifest';
import { registerManifest } from '@workbuddy/services/entities/entity.manifest.registry';
import { OpportunityStageCellRendererComponent } from './presentation/opportunity.stage.cell.renderer';
import { opportunityStageTag } from './presentation/opportunity.stage.colour';
import { navigateToOpportunityRecord } from './services/opportunity.board.open';

const card: EntityManifestCard<Opportunity> = {
  component: () => import('./presentation/card.opportunity').then(m => m.CardOpportunityComponent),
  resolution: 'if-needed',
  isComplete: value => !!value.opportunityNumber,
  summarize: value => ({ title: value.opportunityNumber ?? value.name ?? 'Not set' }),
};

export const opportunityManifest = registerManifest<Opportunity>({
  entity: ActivityEntityName.Opportunity,
  slug: 'opportunity',
  board: {
    defaults: {
      header: { title: 'Opportunities' },
      itemInteraction: { navigate: navigateToOpportunityRecord },
      table: {
        overrideCellRenderer: (_context, _metadata, field, renderer) => field.name === 'stage' ? { ...renderer, cellRenderer: OpportunityStageCellRendererComponent } : renderer,
      },
    },
  },
  presentation: {
    card,
    tags: {
      stage: opportunityStageTag,
    },
  },
  surfaces: {
    [SurfaceType.Creator]: () => import('./surface/creator/opportunity.creator.surface').then(module => module.opportunityCreatorSurface),
    [SurfaceType.Manage]: () => import('./surface/manage/opportunity.manage.surface').then(module => module.opportunityManageSurface),
  },
  operations: {
    create: {
      surface: SurfaceType.Creator,
      presentation: { mode: 'dialog', width: 'min(1200px, calc(100vw - 48px))', height: 'min(800px, calc(100vh - 48px))', maxHeight: 'calc(100vh - 48px)' },
      authorize: ({ injector }) => injector.get(RightsService).hasRights(SalesSecurityRight.ManageOpportunities),
    },
    edit: {
      surface: SurfaceType.Manage,
      // Access mounts the workspace; the surface owns Manage-right and closed-stage readonly policy.
      authorize: ({ injector }) => injector.get(RightsService).hasRights(SalesSecurityRight.CanAccessOpportunities),
    },
  },
  routes: {
    root: '/finance-pro/opportunities',
  },
  relations: {
    [ActivityEntityName.QuotePro]: () => import('./relations/opportunity.quotes.relation').then(module => module.opportunityQuotesRelationBuilder),
    [ActivityEntityName.Task]: () => import('./relations/opportunity.tasks.relation').then(module => module.opportunityTasksRelationBuilder),
    [ActivityEntityName.File]: () => import('./relations/opportunity.files.relation').then(module => module.opportunityFilesRelationBuilder),
    [ActivityEntityName.Activity]: () => import('./relations/opportunity.activities.relation').then(module => module.opportunityActivitiesRelationBuilder),
  },
  domains: {
    [DomainType.ClientPortal]: { presentation: { card } },
    [DomainType.ContractorPortal]: { presentation: { card } },
  },
});
